import dayjs, { Dayjs } from 'dayjs';
import { isCustomAttributeColumnId } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge/identifiers';
import {
  CustomViewFormConditionField,
  CustomViewFormConditionItem,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { ConditionInput, ConditionOperator } from 'src/graphql/types';
import { RelativeDate } from 'src/pages/Settings/rules/components/types';

type DateTimeFields = Pick<CustomViewFormConditionField, 'value' | 'group'>;

const dateTimeFields: DateTimeFields[] = [
  {
    group: 'Conversation',
    value: 'createdAt',
  },
  {
    group: 'Conversation',
    value: 'lastMessageReceivedAt',
  },
  {
    group: 'Site',
    value: 'membershipPausedUntil',
  },
];

type RelativeDatesValues = Record<Exclude<RelativeDate, RelativeDate.CUSTOM>, Dayjs>;

export function handleCustomViewDateFields(
  rule: CustomViewFormConditionItem,
): ConditionInput[] {
  const isDateTimeField = dateTimeFields.find(
    (item) => item.group === rule.field?.group && item.value === rule.field.value,
  );

  const dateValue = getDateFromCustomViewRuleValue(rule?.value as string);

  // custom attributes store instants (the editor writes toISOString), so exact equality on a
  // picked day never matches a date_time value and is timezone-fragile for a date — compare as
  // a day range instead. The picker anchors the picked day at partner-zone midnight, so the
  // range [value, value + 1 day) is exactly the partner-zone calendar day the user sees.
  let builtConditions;
  if (isCustomAttributeColumnId(rule.field?.value ?? '')) {
    builtConditions = buildCustomAttributeDateFilter(rule, rule?.value as string);
  } else if (isDateTimeField) {
    builtConditions = buildDateTimeFilter(rule, dateValue);
  } else {
    builtConditions = buildDateFilter(rule, dateValue);
  }

  const conditions = builtConditions as ConditionInput[];

  const coalesceDefault = rule.field?.coalesceDefault;

  if (coalesceDefault === undefined) {
    return conditions;
  }

  return conditions.map((condition) => ({ ...condition, coalesceDefault }));
}

export function getDateFromCustomViewRuleValue(ruleValue?: string): Dayjs {
  let value: Dayjs;
  const relativeDateValues = getRelativeDateValues();

  if ((ruleValue as keyof RelativeDatesValues) in relativeDateValues) {
    value = relativeDateValues[ruleValue as keyof RelativeDatesValues];
  } else {
    value = ruleValue ? dayjs(ruleValue) : dayjs();
  }

  return value;
}

function buildDateTimeFilter(rule: CustomViewFormConditionItem, dateValue: Dayjs) {
  const valueDateDayLimits: { startOfDay: string; endOfDay: string } = {
    startOfDay: dateValue.startOf('day').format(),
    endOfDay: dateValue.endOf('day').format(),
  };

  const attribute = rule.field?.accessor;

  if (rule.operator === ConditionOperator.DateEquals) {
    return [
      {
        attribute,
        value: valueDateDayLimits.startOfDay,
        operator: ConditionOperator.GreaterThan,
        logicalOperator: 'and',
      },
      {
        attribute,
        value: valueDateDayLimits.endOfDay,
        operator: ConditionOperator.LessThan,
        logicalOperator: 'and',
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateDoesNotEqual) {
    return [
      {
        attribute,
        value: valueDateDayLimits.startOfDay,
        operator: ConditionOperator.LessThan,
        logicalOperator: 'or',
      },
      {
        attribute,
        value: valueDateDayLimits.endOfDay,
        operator: ConditionOperator.GreaterThan,
        logicalOperator: 'or',
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateIsAfter) {
    return [
      {
        attribute,
        value: valueDateDayLimits.endOfDay,
        operator: ConditionOperator.GreaterThan,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateIsBefore) {
    return [
      {
        attribute,
        value: valueDateDayLimits.startOfDay,
        operator: ConditionOperator.LessThan,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateGreaterOrEqualThan) {
    return [
      {
        attribute,
        value: valueDateDayLimits.startOfDay,
        operator: ConditionOperator.GreaterThan,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateLessOrEqualThan) {
    return [
      {
        attribute,
        value: valueDateDayLimits.endOfDay,
        operator: ConditionOperator.LessThan,
      },
    ];
  }

  return rule;
}

function buildDateFilter(rule: CustomViewFormConditionItem, dateValue: Dayjs) {
  const dateString = dateValue.format();
  const attribute = rule.field?.accessor;
  const isCppExpDate = isCppExpDateField(rule.field);

  if (rule.operator === ConditionOperator.DateEquals) {
    return [
      {
        attribute,
        value: dateString,
        operator: ConditionOperator.Equals,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateDoesNotEqual) {
    return [
      {
        attribute,
        value: dateString,
        operator: ConditionOperator.DoesNotEqual,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateIsAfter) {
    return [
      {
        attribute,
        value: dateString,
        operator: ConditionOperator.GreaterThan,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateIsBefore) {
    return [
      {
        attribute,
        value: dateString,
        // CPP Expiration "less than" should include unset dates (null / empty).
        operator: isCppExpDate ? ConditionOperator.LessThanOrNull : ConditionOperator.LessThan,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateGreaterOrEqualThan) {
    return [
      {
        attribute,
        value: dateString,
        operator: ConditionOperator.GreaterOrEqThan,
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateLessOrEqualThan) {
    return [
      {
        attribute,
        value: dateString,
        operator: isCppExpDate
          ? ConditionOperator.LessOrEqThanOrNull
          : ConditionOperator.LessOrEqThan,
      },
    ];
  }

  return rule;
}

function isCppExpDateField(field?: CustomViewFormConditionField): boolean {
  const accessor = field?.accessor ?? '';
  const value = field?.value ?? '';

  return (
    value === 'cppExpDate' ||
    accessor === 'cppExpDate' ||
    accessor.endsWith('.cppExpDate') ||
    accessor.endsWith('/cppExpDate')
  );
}

function buildCustomAttributeDateFilter(
  rule: CustomViewFormConditionItem,
  ruleValue: string | undefined,
) {
  const isRelative = (ruleValue as keyof RelativeDatesValues) in getRelativeDateValues();
  const anchor = getDateFromCustomViewRuleValue(ruleValue);

  const start = isRelative ? anchor.startOf('day') : anchor;
  const dayStart = start.toISOString();
  const nextDayStart = start.add(1, 'day').toISOString();

  const attribute = rule.field?.accessor;

  if (rule.operator === ConditionOperator.DateEquals) {
    return [
      {
        attribute,
        value: dayStart,
        operator: ConditionOperator.GreaterOrEqThan,
        logicalOperator: 'and',
      },
      {
        attribute,
        value: nextDayStart,
        operator: ConditionOperator.LessThan,
        logicalOperator: 'and',
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateDoesNotEqual) {
    return [
      {
        attribute,
        value: dayStart,
        operator: ConditionOperator.LessThan,
        logicalOperator: 'or',
      },
      {
        attribute,
        value: nextDayStart,
        operator: ConditionOperator.GreaterOrEqThan,
        logicalOperator: 'or',
      },
    ];
  }

  if (rule.operator === ConditionOperator.DateIsAfter) {
    return [{ attribute, value: nextDayStart, operator: ConditionOperator.GreaterOrEqThan }];
  }

  if (rule.operator === ConditionOperator.DateIsBefore) {
    return [{ attribute, value: dayStart, operator: ConditionOperator.LessThan }];
  }

  if (rule.operator === ConditionOperator.DateGreaterOrEqualThan) {
    return [{ attribute, value: dayStart, operator: ConditionOperator.GreaterOrEqThan }];
  }

  if (rule.operator === ConditionOperator.DateLessOrEqualThan) {
    return [{ attribute, value: nextDayStart, operator: ConditionOperator.LessThan }];
  }

  return rule;
}

function getRelativeDateValues(): RelativeDatesValues {
  return {
    [RelativeDate.TODAY]: dayjs(),
    [RelativeDate.TOMORROW]: dayjs().add(1, 'day'),
    [RelativeDate.YESTERDAY]: dayjs().subtract(1, 'day'),
    [RelativeDate.ONE_WEEK_FROM_NOW]: dayjs().add(7, 'day'),
    [RelativeDate.ONE_WEEK_AGO]: dayjs().subtract(7, 'day'),
    [RelativeDate.THIRTY_DAYS_AGO]: dayjs().subtract(30, 'day'),
    [RelativeDate.SIXTY_DAYS_AGO]: dayjs().subtract(60, 'day'),
    [RelativeDate.NINETY_DAYS_AGO]: dayjs().subtract(90, 'day'),
  };
}
